import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToasterService, ToasterConfig } from 'angular2-toaster';
import { CookieService } from 'angular2-cookie/core';
import { Subscription } from 'rxjs';

import { ApplicationStateService } from '../services/application-state.service';
import { OrderInformationService } from '../services/order-information.service';
import { LoadingSpinnerService } from '../services/loading-spinner.service';
import { PageTitleService } from '../services/page-title.service';
import { QuoteInfoService } from '../services/quote-info.service';
import { QuoteSummaryService } from '../services/quote-summary.service';
import { CurrentUserService } from '../services/current-user.service';
import { ModalDialogService } from '../reusable-components/modal-dialog/modal-dialog.service';
import { AdjustmentService } from '../services/adjustments.service';
import { BrowserWidthService } from '../services/browser-width.service';
import { HttpResponseService } from '../services/httpresponse-service';
import { QuoteRevisionService } from '../services/quote-revision.service';

import { OrderInformation } from '../model/order-information';
import { Constants } from '../model/constants';
import { Quote } from '../model/quote';
import { Adjustment } from '../model/adjustment';
import { QuoteSummary } from '../model/quote-summary';
import { QuoteRevision } from '../model/quote-revision';
import { SplunkLogEventType, SplunkLogStatement } from '../model/splunk-log-statement';
import { SplunkLogService } from '../services/splunk-log.service';

@Component({
  selector: 'my-order-information-configuration',
  template: require('./order-information.component.html'),
  styles: [require('./order-information.component.scss')]
})
export class OrderInformationComponent implements OnInit, OnDestroy {
  quoteId: number;
  currentPricingView: number;
  baseAmount: number;
  customerTransportation: number = 0;
  customerSurcharge: number = 0;
  currentUserId: number;
  splunkLogStatement: SplunkLogStatement;
  stateError: string = '';
  modifiedBy: string = '';
  retailerName: string = '';
  quoteSummary: QuoteSummary = null;
  initialFreightOverride: number = 0;

  orderInfo: OrderInformation;
  quote: Quote;
  config: ToasterConfig = new ToasterConfig({
    positionClass: 'toast-bottom-right'
  });
  currentAdjustment: Adjustment = null;
  availableRetailers: Array<any>;

  normalUser: boolean = false;
  facilityUser: boolean = false;
  retailUser: boolean = false;
  stateIsInvalid: boolean = false;
  isValidSquareFoot: boolean = true;
  isValidSurcharge: boolean = true;
  isValidFreightCharge: boolean = true;
  showingDeactivateModal: boolean = false;
  disableSaveOnDestroy: boolean = false;
  subscription: Subscription = new Subscription();

  constructor(
    public applicationStateService: ApplicationStateService,
    public orderInfoService: OrderInformationService,
    public route: ActivatedRoute,
    public toasterService: ToasterService,
    public router: Router,
    public currentUserService: CurrentUserService,
    public spinner: LoadingSpinnerService,
    public pageTitleService: PageTitleService,
    public quoteInfoService: QuoteInfoService,
    public quoteSummaryService: QuoteSummaryService,
    public modalDialogService: ModalDialogService,
    public cookieService: CookieService,
    private adjustmentService: AdjustmentService,
    public browserWidthService: BrowserWidthService,
    public httpResponseService: HttpResponseService,
    public quoteRevisionService: QuoteRevisionService,
    public splunkLogService: SplunkLogService
  ) {
    const routeParamsSub = this.route.params.subscribe(x => {
      this.quoteId = x['quoteId'];
      this.quoteInfoService.quoteId$.next(this.quoteId);
    });
    this.subscription.add(routeParamsSub);
    this.pageTitleService.pageTitle.next({
      title: Constants.CmhConnectionTitle
    });

    this.splunkLogStatement = new SplunkLogStatement();
    const currentUserSub = this.currentUserService.currentUser$.subscribe(
      cu => {
        this.currentUserId = cu.cmhUserId || cu.CmhUserId;
        this.splunkLogStatement.SetUser(this.currentUserId, cu.userName, 
            currentUserService.getUserType(cu.isPlantUser, cu.isInternal));
        this.splunkLogStatement.SetRetailer(cu.retailerNumber, cu.retailerName, cu.currentRetailer.retailerType);
        
        this.modifiedBy = cu.cmhUserId + '-' + cu.firstName + ' ' + cu.lastName;
        this.availableRetailers = cu.availableRetailers;
        if (this.orderInfo !== undefined && this.orderInfo !== null) {
          this.orderInfo.ModifiedBy = this.modifiedBy;
        }
        if (cu.isPlantUser) {
          this.setFacilityUser();
        } else {
          this.setRetailUser();
        }
      }
    );
    this.subscription.add(currentUserSub);
    const currentPricingViewSub = this.quoteInfoService.currentPricingView$.subscribe(
      x => {
        this.currentPricingView = x;

        if (this.orderInfo !== undefined && this.orderInfo !== null) {
          this.orderInfo.BaseAmount = this.baseAmount;
        }
      }
    );
    this.subscription.add(currentPricingViewSub);

    const quoteSummarySub = this.quoteInfoService.quoteSummary$.subscribe(
      x => {
        this.quoteSummary = x;
        if (this.currentPricingView === 1) {
          if (x !== undefined && x !== null) {
            this.baseAmount =
              x.ModelBaseCost +
              x.Dues -
              this.customerTransportation -
              this.customerSurcharge;
          } else {
            this.baseAmount = 0.0;
          }
        } else {
          this.baseAmount = x.ModelBaseCost;
        }
      },
      error => {
        this.httpResponseService.httpErrorResponse$.next(error);
      }
    );
    this.subscription.add(quoteSummarySub);
  }

  ngOnInit() {
    this.spinner.showSpinner();
    this.orderInfo = new OrderInformation();

    if (this.quoteId === undefined || this.quoteId === 0) {
      this.spinner.hideSpinner();
      this.stateIsInvalid = true;
      this.modalDialogService
        .showMessage({
          headerText: 'Order Information',
          message: Constants.NewQuoteMessage
        })
        .subscribe(messageReply => {
          this.redirectRequired(true, false, false);
        });
    } else {
      const refreshStateFromDbSub = this.applicationStateService
        .refreshStateFromDB(this.quoteId, true)
        .subscribe(
          dbQuote => {
            if (dbQuote !== undefined && dbQuote !== null) {
              this.pageTitleService.pageTitle.next({
                title: Constants.CmhConnectionTitle,
                subTitle:
                  Constants.CurrentQuoteSubTitle +
                  (dbQuote.iseriesQuoteNumber > 0 && dbQuote.QuoteStatusesID > 0
                    ? dbQuote.iseriesQuoteNumber
                    : this.quoteId)
              });
              this.quoteInfoService.quote$.next(dbQuote);
              this.quote = dbQuote;
              const canAccessQuoteSub = this.quoteInfoService.canAccessQuote$.subscribe(
                canAccessQuote => {
                  if (canAccessQuote === true) {
                    if (
                      this.quote.productLineId === undefined ||
                      this.quote.productLineId === null ||
                      this.quote.productLineId === 0
                    ) {
                      this.spinner.hideSpinner();
                      this.stateIsInvalid = true;
                      this.modalDialogService
                        .showMessage({
                          headerText: Constants.NoProductLineHeader,
                          message: Constants.NoProductLineMessage
                        })
                        .subscribe(messageReply => {
                          this.redirectRequired(false, true, false);
                        });
                    } else {
                      if (
                        this.quote.modelId === undefined ||
                        this.quote.modelId === null ||
                        this.quote.modelId === 0
                      ) {
                        this.spinner.hideSpinner();
                        this.stateIsInvalid = true;
                        this.modalDialogService
                          .showMessage({
                            headerText: Constants.NoModelHeader,
                            message: Constants.NoModelMessage
                          })
                          .subscribe(messageReply => {
                            this.redirectRequired(false, false, true);
                          });
                      } else {
                        this.quoteSummaryService
                          .getPricingHeader(dbQuote.quoteId)
                          .subscribe(
                            x => {
                              this.customerSurcharge = x.Surcharge;
                              this.customerTransportation = x.Transportation;
                              this.currentAdjustment =
                                x.AdjustmentInfo !== undefined &&
                                x.AdjustmentInfo !== null
                                  ? x.AdjustmentInfo
                                  : null;
                              this.orderInfoService
                                .getOrderInfo(dbQuote.quoteId)
                                .map(x => {
                                  this.orderInfo = x;
                                  this.initialFreightOverride =
                                    this.orderInfo.FreightOverride ===
                                      undefined ||
                                    this.orderInfo.FreightOverride === null ||
                                    this.orderInfo.FreightOverride === ''
                                      ? 0
                                      : Number(this.orderInfo.FreightOverride);

                                  if (
                                    this.availableRetailers !== undefined &&
                                    this.availableRetailers.length > 0
                                  ) {
                                    this.retailerName = this.availableRetailers.find(
                                      retailer =>
                                        retailer.retailerNumber ===
                                        this.orderInfo.RetailerShipTo
                                    ).name;
                                  }
                                  this.orderInfo.ModifiedBy = this.modifiedBy;
                                  this.orderInfo.BaseAmount =
                                    this.quoteInfoService.quoteSummary$ !==
                                      null &&
                                    this.quoteInfoService.quoteSummary$.value
                                      .ModelBaseCost !== undefined
                                      ? this.quoteInfoService.quoteSummary$
                                          .value.ModelBaseCost
                                      : this.orderInfo.BaseAmount;
                                })
                                .subscribe(
                                  data => {},
                                  error => {
                                    this.spinner.hideSpinner();
                                    this.httpResponseService.httpErrorResponse$.next(
                                      error
                                    );
                                  },
                                  () => {
                                    this.spinner.hideSpinner();
                                  }
                                );
                            },
                            error => {
                              this.spinner.hideSpinner();
                              this.httpResponseService.httpErrorResponse$.next(
                                error
                              );
                            },
                            () => {}
                          );
                      }
                    }
                  } else {
                    this.spinner.hideSpinner();
                  }
                }
              );
              this.subscription.add(canAccessQuoteSub);
            } else {
              this.spinner.hideSpinner();
              this.stateIsInvalid = true;
              this.modalDialogService
                .showMessage({
                  headerText: 'Order Information',
                  message: Constants.QuoteNotFoundMessage.replace(
                    '#quoteid#',
                    this.quoteId.toString()
                  )
                })
                .subscribe(messageReply => {
                  this.redirectRequired(true, false, false);
                });
            }
          },
          error => {
            this.spinner.hideSpinner();
            this.httpResponseService.httpErrorResponse$.next(error);
          }
        );
      this.subscription.add(refreshStateFromDbSub);
    }
  }

  ngOnDestroy() {
    if (!this.disableSaveOnDestroy) {
      this.saveOrderInfoOnDestroy();
    }
    this.subscription.unsubscribe();
  }

  canDeactivate(): boolean {
    if (
      this.quoteInfoService.canAccessQuote$.value === true &&
      !this.stateIsInvalid
    ) {
      this.validateAdjustedSquareFootage();
      this.validateFreightOverride();
      this.validateSurchargeOverride();
      if (
        this.isValidFreightCharge &&
        this.isValidSurcharge &&
        this.isValidSquareFoot
      ) {
        return true;
      } else {
        if (!this.showingDeactivateModal) {
          this.showingDeactivateModal = true;
          this.modalDialogService
            .showMessage({
              headerText: 'Order Information',
              message: Constants.ValidationErrorsMessage
            })
            .subscribe(messageReply => {
              this.showingDeactivateModal = false;
            });
        }
        return false;
      }
    } else {
      return true;
    }
  }

  saveNext() {
    this.splunkLogStatement.SetEvent(SplunkLogEventType.QuoteSaveAndNext);
    this.splunkLogStatement.SetQuote(this.quoteId, this.quote.modelId, this.quoteSummary.ModelName, this.quote.plantId);
    this.splunkLogService.log(this.splunkLogStatement).subscribe();

    if (typeof this.orderInfo !== 'undefined' && this.orderInfo !== null) {
      if (
        this.isValidSurcharge &&
        this.isValidSquareFoot &&
        this.isValidFreightCharge
      ) {
        this.disableSaveOnDestroy = true;
        this.spinner.showSpinner();
        this.orderInfo.QuoteId = this.quoteId;
        let quote = this.applicationStateService.getQuote();
        this.orderInfo.QuoteRevisionNumber = quote.revisionNumber;
        this.orderInfoService
          .insertOrderInfo(this.orderInfo)
          .map(x => {
            this.toasterService.pop(
              'success',
              'Success!',
              'Order information has been saved'
            );
            /// TODO: add code to check adjustments if override entered
            if (
              this.orderInfo.ModelBaseOverride > 0 &&
              this.currentAdjustment !== null
            ) {
              // recalculate adjustment based on override entered
              let maxBaseCost = this.orderInfo.ModelBaseOverride * 0.7;
              if (this.currentAdjustment.baseAdjustmentFlat > 0) {
                // validate flat amount and if invalid update
                if (this.currentAdjustment.baseAdjustmentFlat > maxBaseCost) {
                  this.adjustmentService.updateAdjustment(
                    this.currentAdjustment
                  );
                }
              }
            }
          })
          .subscribe(
            data => {
              // now update quote pricing from quote summary order info
              if (
                Number(this.initialFreightOverride) > 0 &&
                Number(this.orderInfo.FreightOverride) === 0
              ) {
                // have to retrieve pricing header to get new calculated transportation cost.
                this.quoteSummaryService
                  .getPricingHeader(this.orderInfo.QuoteId)
                  .subscribe(
                    x => {
                      let qs = x;
                      qs.ModelBaseCost =
                        qs.ModelBaseCost === undefined ||
                        qs.ModelBaseCost === null
                          ? 0
                          : qs.ModelBaseCost;
                      qs.ModelBaseOverride =
                        Number(this.orderInfo.ModelBaseOverride) > 0
                          ? Number(this.orderInfo.ModelBaseOverride)
                          : 0;
                      qs.Transportation = x.Transportation;
                      qs.DuesOnly =
                        qs.DuesOnly === undefined || qs.DuesOnly === null
                          ? 0
                          : qs.DuesOnly;
                      qs.OptionsTotal =
                        qs.OptionsTotal === undefined ||
                        qs.OptionsTotal === null
                          ? 0
                          : qs.OptionsTotal;
                      qs.Surcharge =
                        qs.Surcharge === undefined || qs.Surcharge === null
                          ? 0
                          : qs.Surcharge;
                      qs.Taxes =
                        qs.Taxes === undefined || qs.Taxes === null
                          ? 0
                          : qs.Taxes;
                      qs.Transportation =
                        qs.Transportation === undefined ||
                        qs.Transportation === null
                          ? 0
                          : qs.Transportation;

                      this.applicationStateService.setQuotePricingAndUpdate(
                        qs.ModelBaseCost,
                        qs.ModelBaseOverride,
                        qs.DuesOnly,
                        qs.OptionsTotal,
                        qs.VIPDiscountAmount,
                        qs.PartnerAssistanceAmount,
                        qs.Surcharge,
                        qs.Taxes,
                        qs.Transportation,
                        qs.GrandTotal
                      );
                    },
                    error => {
                      this.spinner.hideSpinner();
                      this.httpResponseService.httpErrorResponse$.next(error);
                    }
                  );
              } else {
                let qs = this.quoteSummary;
                let freightOverride =
                  this.orderInfo.FreightOverride === undefined ||
                  this.orderInfo.FreightOverride === null ||
                  this.orderInfo.FreightOverride === ''
                    ? 0
                    : Number(this.orderInfo.FreightOverride);
                qs.ModelBaseCost =
                  qs.ModelBaseCost === undefined || qs.ModelBaseCost === null
                    ? 0
                    : qs.ModelBaseCost;
                qs.ModelBaseOverride =
                  Number(this.orderInfo.ModelBaseOverride) > 0
                    ? Number(this.orderInfo.ModelBaseOverride)
                    : 0;
                qs.Transportation =
                  freightOverride > 0 ? freightOverride : qs.Transportation;
                qs.DuesOnly =
                  qs.DuesOnly === undefined || qs.DuesOnly === null
                    ? 0
                    : qs.DuesOnly;
                qs.OptionsTotal =
                  qs.OptionsTotal === undefined || qs.OptionsTotal === null
                    ? 0
                    : qs.OptionsTotal;
                qs.Surcharge =
                  qs.Surcharge === undefined || qs.Surcharge === null
                    ? 0
                    : qs.Surcharge;
                qs.Taxes =
                  qs.Taxes === undefined || qs.Taxes === null ? 0 : qs.Taxes;
                qs.Transportation =
                  qs.Transportation === undefined || qs.Transportation === null
                    ? 0
                    : qs.Transportation;

                this.applicationStateService.setQuotePricingAndUpdate(
                  qs.ModelBaseCost,
                  qs.ModelBaseOverride,
                  qs.DuesOnly,
                  qs.OptionsTotal,
                  qs.VIPAmount,
                  qs.PartnerAssistanceAmount,
                  qs.Surcharge,
                  qs.Taxes,
                  qs.Transportation,
                  qs.GrandTotal
                );
              }
            },
            error => {
              this.spinner.hideSpinner();
              this.httpResponseService.httpErrorResponse$.next(error);
            },
            () => {
              this.router.navigate(['quote-summary', this.quote.quoteId]);
            }
          );
      } else {
        this.validateAdjustedSquareFootage();
        this.validateFreightOverride();
        this.validateSurchargeOverride();
      }
    }
  }

  saveOrderInfoOnDestroy() {
    if (typeof this.orderInfo !== 'undefined' && this.orderInfo !== null) {
      if (
        this.isValidSurcharge &&
        this.isValidSquareFoot &&
        this.isValidFreightCharge
      ) {
        this.spinner.showSpinner();
        this.orderInfo.QuoteId = this.quoteId;
        this.orderInfo.ModelBaseOverride =
          this.orderInfo.ModelBaseOverride === 0
            ? null
            : this.orderInfo.ModelBaseOverride;
        let quote = this.applicationStateService.getQuote();
        this.orderInfo.QuoteRevisionNumber = quote.revisionNumber;
        this.orderInfoService.insertOrderInfo(this.orderInfo).subscribe(
          data => {
            this.orderInfo = data;
            this.orderInfo.ModifiedBy = this.modifiedBy;
            this.orderInfo.BaseAmount =
              this.quoteInfoService.quoteSummary$ !== null &&
              this.quoteInfoService.quoteSummary$.value.ModelBaseCost !==
                undefined
                ? this.quoteInfoService.quoteSummary$.value.ModelBaseCost
                : this.orderInfo.BaseAmount;

            // now update quote pricing from quote summary order info
            if (
              Number(this.initialFreightOverride) > 0 &&
              Number(this.orderInfo.FreightOverride) === 0
            ) {
              // have to retrieve pricing header to get new calculated transportation cost.
              this.quoteSummaryService
                .getPricingHeader(this.orderInfo.QuoteId)
                .subscribe(
                  x => {
                    let qs = x;
                    qs.ModelBaseCost =
                      qs.ModelBaseCost === undefined ||
                      qs.ModelBaseCost === null
                        ? 0
                        : qs.ModelBaseCost;
                    qs.ModelBaseOverride =
                      Number(this.orderInfo.ModelBaseOverride) > 0
                        ? Number(this.orderInfo.ModelBaseOverride)
                        : 0;
                    qs.Transportation = x.Transportation;
                    qs.DuesOnly =
                      qs.DuesOnly === undefined || qs.DuesOnly === null
                        ? 0
                        : qs.DuesOnly;
                    qs.OptionsTotal =
                      qs.OptionsTotal === undefined || qs.OptionsTotal === null
                        ? 0
                        : qs.OptionsTotal;
                    qs.Surcharge =
                      qs.Surcharge === undefined || qs.Surcharge === null
                        ? 0
                        : qs.Surcharge;
                    qs.Taxes =
                      qs.Taxes === undefined || qs.Taxes === null
                        ? 0
                        : qs.Taxes;
                    qs.Transportation =
                      qs.Transportation === undefined ||
                      qs.Transportation === null
                        ? 0
                        : qs.Transportation;

                    this.applicationStateService.setQuotePricingAndUpdate(
                      qs.ModelBaseCost,
                      qs.ModelBaseOverride,
                      qs.DuesOnly,
                      qs.OptionsTotal,
                      qs.VIPDiscountAmount,
                      qs.PartnerAssistanceAmount,
                      qs.Surcharge,
                      qs.Taxes,
                      qs.Transportation,
                      qs.GrandTotal
                    );
                  },
                  error => {
                    this.spinner.hideSpinner();
                    this.httpResponseService.httpErrorResponse$.next(error);
                  }
                );
            } else {
              // now update quote pricing from quote summary order info
              let qs = this.quoteSummary;
              let freightOverride =
                this.orderInfo.FreightOverride === undefined ||
                this.orderInfo.FreightOverride === null ||
                this.orderInfo.FreightOverride === ''
                  ? 0
                  : Number(this.orderInfo.FreightOverride);
              qs.ModelBaseCost = this.orderInfo.BaseAmount;
              qs.ModelBaseOverride =
                Number(this.orderInfo.ModelBaseOverride) > 0
                  ? Number(this.orderInfo.ModelBaseOverride)
                  : 0;
              qs.Transportation = freightOverride;
              qs.ModelBaseCost =
                qs.ModelBaseCost === undefined || qs.ModelBaseCost === null
                  ? 0
                  : qs.ModelBaseCost;
              qs.ModelBaseOverride =
                this.orderInfo.ModelBaseOverride > 0
                  ? this.orderInfo.ModelBaseOverride
                  : 0;
              qs.Transportation = freightOverride;
              qs.DuesOnly =
                qs.DuesOnly === undefined || qs.DuesOnly === null
                  ? 0
                  : qs.DuesOnly;
              qs.OptionsTotal =
                qs.OptionsTotal === undefined || qs.OptionsTotal === null
                  ? 0
                  : qs.OptionsTotal;
              qs.Surcharge =
                qs.Surcharge === undefined || qs.Surcharge === null
                  ? 0
                  : qs.Surcharge;
              qs.Taxes =
                qs.Taxes === undefined || qs.Taxes === null ? 0 : qs.Taxes;
              qs.Transportation =
                qs.Transportation === undefined || qs.Transportation === null
                  ? 0
                  : qs.Transportation;

              this.applicationStateService.setQuotePricingAndUpdate(
                qs.ModelBaseCost,
                qs.ModelBaseOverride,
                qs.DuesOnly,
                  qs.OptionsTotal,
                  qs.VIPAmount,
                  qs.PartnerAssistanceAmount,
                qs.Surcharge,
                qs.Taxes,
                qs.Transportation,
                qs.GrandTotal
              );
            }
          },
          error => {
            this.spinner.hideSpinner();
            this.httpResponseService.httpErrorResponse$.next(error);
          }
        );
      }
    }
  }

  validateAdjustedSquareFootage() {
    if (
      isNaN(this.orderInfo.AdjustedSquareFootage) ||
      this.orderInfo.AdjustedSquareFootage < -99999 ||
      this.orderInfo.AdjustedSquareFootage > 99999
    ) {
      this.isValidSquareFoot = false;
    } else {
      this.isValidSquareFoot = true;
    }
  }

  validateSurchargeOverride() {
    if (
      Number(this.orderInfo.SurchargeOverride) < -99999.99 ||
      Number(this.orderInfo.SurchargeOverride) > 99999.99
    ) {
      this.isValidSurcharge = false;
    } else {
      this.isValidSurcharge = true;
    }
  }

  validateFreightOverride() {
    if (
      Number(this.orderInfo.FreightOverride) < 0.0 ||
      Number(this.orderInfo.FreightOverride) > 99999.99
    ) {
      this.isValidFreightCharge = false;
    } else {
      this.isValidFreightCharge = true;
    }
  }

  reset() {
    let RetailerShipTo = this.orderInfo.RetailerShipTo;
    let BaseAmount = this.orderInfo.BaseAmount;

    let quote = this.applicationStateService.getQuote();
    //  this.orderInfo = this.orderInfoService.resetOrderInfo(quote.quoteId, this.orderInfo.OrderInfoId, this.orderInfo.ModifiedBy);
    this.orderInfo = this.orderInfoService.resetOrderInfo(
      quote.quoteId,
      this.orderInfo
    );

    this.orderInfo.BaseAmount = BaseAmount;
    this.orderInfo.RetailerShipTo = RetailerShipTo;

    this.isValidFreightCharge = true;
    this.isValidSquareFoot = true;
    this.isValidSurcharge = true;
  }

  setFacilityUser() {
    this.normalUser = false;
    this.facilityUser = true;
    this.retailUser = false;
    this.isValidFreightCharge = true;
    this.isValidSquareFoot = true;
    this.isValidSurcharge = true;
  }

  setRetailUser() {
    this.normalUser = false;
    this.facilityUser = false;
    this.retailUser = true;
    this.isValidFreightCharge = true;
    this.isValidSquareFoot = true;
    this.isValidSurcharge = true;
  }

    redirectRequired(newQuotesRedirect: boolean, productLinesRedirect: boolean, modelsRedirect: boolean) {
        this.stateIsInvalid = true;
        if (newQuotesRedirect) {
            this.router.navigate([Constants.NewQuotesUrl]);
        }
        if (productLinesRedirect) {
            this.router.navigate([Constants.ProductLineUrl.replace('#quoteid#', this.quoteId.toString())]);
        }
        if (modelsRedirect) {
            this.router.navigate([Constants.ModelUrl.replace('#quoteid#', this.quoteId.toString())]);
        }
	}

	keyPress(event: any) {
		const pattern = /[0-9\+\-\ ]/;
		const pattern2 = /^-?(\d|,)*\.?\d*$/;

		let inputChar = String.fromCharCode(event.charCode);
		if (event.keyCode != 8 && !pattern2.test(inputChar)) {
			event.preventDefault();
		}
	}
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/order-information/order-information.component.ts